import { useState } from 'react';
import { Add, DeleteOutlined } from '@mui/icons-material';
import { Box, Button, Grid, Tab, Tabs } from '@mui/material';
import { ConfirmModal, LogoElement, UserPermission } from 'remote/global';
import { useTranslation } from 'react-i18next';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { Outlet, Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { hasWritePermission } from 'utils/helpers';
import ComposedDrawerMenuAppBar from 'components/ComposedDrawerMenuAppBar';
import { ROUTES } from 'utils/data/routes';
import {
  useDeleteOperatorsDriversMutation,
  useOperatorsDriversQuery,
  usePostOperatorsDriversInvitationMutation,
} from 'api/queries/operators';
import VacationModal from 'components/modals/DriverVacationModal';

function DriversDetailView() {
  const [isVacationModalOpen, setIsVacationModalOpen] = useState(false);
  const [isDeleteDriverModalOpen, setIsDeleteDriverModalOpen] = useState(false);
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: driver } = useOperatorsDriversQuery(id!);
  const { permissions = {} } = userInfo ?? {};
  const firstName = driver?.contactInfo?.firstName;
  const lastName = driver?.contactInfo?.lastName;
  const fullName = driver ? `${firstName} ${lastName}` : '';
  const hasAccount = driver?.hasAccount;
  const postOperatorsDriversInvitationMutation = usePostOperatorsDriversInvitationMutation(id!);
  const deleteOperatorsDriversMutation = useDeleteOperatorsDriversMutation(id!);

  const location = useLocation();
  const currentTab = location.pathname.split('/').pop();

  const handleInviteToApp = async () => {
    await postOperatorsDriversInvitationMutation.mutateAsync();
    toast.success(t('Toast.emailSent'));
  };

  const handleDeleteDriver = async () => {
    try {
      await deleteOperatorsDriversMutation.mutateAsync();
      navigate([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.DRIVERS].join('/'), {
        replace: true,
      });
      toast.success(t('Toast.success'));
    } catch (error) {
      // TODO: Handle error
    }
  };

  return (
    <>
      <ConfirmModal
        open={isDeleteDriverModalOpen}
        setOpen={setIsDeleteDriverModalOpen}
        onClose={() => setIsDeleteDriverModalOpen(false)}
        onConfirm={handleDeleteDriver}
        description={t('modals.deleteDriver.subtitle', { name: fullName }).toString()}
      />
      <VacationModal
        open={isVacationModalOpen}
        setOpen={setIsVacationModalOpen}
        onClose={() => setIsVacationModalOpen(false)}
        defaultValues={{
          ownerId: Number(id),
        }}
      />
      <ComposedDrawerMenuAppBar title={fullName} />
      <Box sx={{ p: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Grid container gap={1}>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              mb: 1,
              gap: 1,
            }}
          >
            <Button
              variant='outlined'
              onClick={() => setIsVacationModalOpen(true)}
              startIcon={<Add />}
              disabled={!hasWritePermission(permissions?.DRIVER_SCHEDULE)}
            >
              {t('DriversView.actions.addDriverToVacation')}
            </Button>
            <Button
              variant='outlined'
              onClick={() => setIsDeleteDriverModalOpen(true)}
              startIcon={<DeleteOutlined />}
              disabled={!hasWritePermission(permissions?.DRIVER_DETAILS)}
            >
              {t('DriversView.actions.deleteAccount')}
            </Button>
            {!hasAccount &&
              hasWritePermission(permissions?.DRIVER_DETAILS) &&
              ![UserPermission.RESTRICTED].includes(permissions?.DRIVER_INVITATION) && (
                <Button variant='contained' onClick={handleInviteToApp}>
                  {t('DriversView.actions.inviteDriverToApp')}
                </Button>
              )}
          </Grid>
          <Grid item xs={12}>
            <Tabs
              sx={{
                '& .MuiTabs-flexContainer': {
                  alignItems: { xs: 'center', md: 'flex-start' },
                },
              }}
              variant='scrollable'
              value={currentTab}
            >
              <Tab
                label={t('DriversView.DetailView.tabs.details')}
                component={RouterLink}
                to=''
                value={id}
              />
              <Tab
                label={t('DriversView.DetailView.tabs.documents')}
                component={RouterLink}
                to={ROUTES.DriversRoutes.DOCUMENTS}
                value={ROUTES.DriversRoutes.DOCUMENTS}
              />
              <Tab
                label={t('DriversView.DetailView.tabs.vacations')}
                component={RouterLink}
                to={ROUTES.DriversRoutes.VACATION}
                value={ROUTES.DriversRoutes.VACATION}
              />
            </Tabs>
          </Grid>
        </Grid>

        <LogoElement
          color='red'
          sx={{
            pointerEvents: 'none',
            position: 'fixed',
            right: '-126px',
            transform: 'rotate(12deg)',
          }}
        />
        <Outlet />
      </Box>
    </>
  );
}

export default DriversDetailView;
